import React, { memo, useMemo } from 'react';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { Box } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import { STORY_LEVEL } from '@atlassian/jira-portfolio-3-common/src/hierarchy/index.tsx';
import SelectWireframe from '@atlassian/jira-portfolio-3-common/src/select-wireframe/index.tsx';
import type { OptionType } from '@atlassian/jira-portfolio-3-common/src/select/types.tsx';
import { CellSkeleton } from '@atlassian/jira-portfolio-3-common/src/skeleton/cell.tsx';
import type { LazyGoal } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/issue-goals/types';
import type { ScopeIssue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/scope/types.tsx';
import { DEFAULT_FIELD_WIDTH } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import GoalCellSelect from '@atlassian/jira-portfolio-3-portfolio/src/common/view/goal/goal-cell-select';
import type { Props } from './types';

const calculateFlexShrink = (lazyGoals: LazyGoal[]): number[] => {
	const goalNameLengths = lazyGoals.map((lazyGoal) =>
		lazyGoal.goal === undefined ? 0 : lazyGoal.goal.name.length,
	);
	const shrinkCoefficient = 0.2; // A smaller number will make the goal components shrink closer to a similar rate.
	return goalNameLengths.map((length) => Math.pow(length, shrinkCoefficient));
};

const AllAtlasGoals = memo<Props>(
	({
		hasAtlasPermissions,
		isScrolling = false,
		issue,
		onChange: onChangeApiUpdate,
		isExportMode,
		lazyGoalsByARI,
		...props
	}: Props) => {
		const goals = useMemo(() => issue.goals ?? [], [issue]);
		const lazyGoals = useMemo(
			() =>
				goals
					.map((goalARI) => lazyGoalsByARI[goalARI])
					.filter((lazyGoal) => lazyGoal !== undefined),
			[goals, lazyGoalsByARI],
		);

		if (lazyGoals.some(({ isLoading }) => isLoading)) {
			return <CellSkeleton width={DEFAULT_FIELD_WIDTH.MEDIUM} />;
		}
		const filteredLazyGoals = lazyGoals.filter((lazyGoal) => lazyGoal.goal !== undefined);

		const canEditGoals =
			hasAtlasPermissions &&
			(ff('hrz-716-integrate-goal-picker-into-issue-view') ? true : issue.level > STORY_LEVEL);

		if (isScrolling) {
			return <SelectWireframe />;
		}

		const flexShrinkValues = calculateFlexShrink(lazyGoals);

		// eslint-disable-next-line @typescript-eslint/no-shadow
		const onChange = (issue: ScopeIssue, goals: OptionType[]) => {
			if (!isEqual(filteredLazyGoals, goals)) {
				onChangeApiUpdate(issue, goals);
			}
		};

		const currentGoals = issue.goals;
		const originalGoals = issue.originals?.goals;
		const hasBeenChanged =
			(originalGoals && !isEmpty(difference(currentGoals, originalGoals))) ||
			(currentGoals && !isEmpty(difference(originalGoals, currentGoals)));

		if (canEditGoals) {
			return (
				<Box>
					<GoalCellSelect
						{...props}
						isCellDirty={() => hasBeenChanged}
						issue={issue}
						onChange={onChange}
						flexShrinkValues={flexShrinkValues}
						lazyGoals={filteredLazyGoals}
						isExportMode={isExportMode}
						goals={lazyGoals}
					/>
				</Box>
			);
		}
		return null;
	},
);

export default AllAtlasGoals;
